import { useTar } from '@/modules'
import { Card, CardContent, CardHeader, CardTitle, Switch } from '@duck/ui'
import { useState, useEffect } from 'react'
import { useOrganizationSettings } from '@/hooks/useOrganizationSettings'
import { PartnerService } from '@duck/api-definitions'
import { SettingRow } from '@/components/settings'
import { LanguagePicker, type FeedbackLanguage } from '@/components/LanguagePicker'
import { usePartnerTar } from '@/hooks/usePartnerTar'
import { Bell, Mail, Languages } from 'lucide-react'
import { toast } from 'sonner'
import { AxiosError } from 'axios'

export const OrganizationSettingsPage = () => {
    const { t } = useTar()
    const { tP } = usePartnerTar()
    const { setOrgSetting, isLoading, settings } = useOrganizationSettings()

    const [localSettings, setLocalSettings] = useState<PartnerService.OrganizationSettings>({})

    useEffect(() => {
        if (settings) {
            setLocalSettings(settings)
        }
    }, [settings])

    const handleSettingChange = async (
        key: keyof PartnerService.OrganizationSettings,
        value: boolean | PartnerService.OrganizationSettingsFeedbackLanguageEnum
    ) => {
        const prev = { ...localSettings }
        setLocalSettings((s) => ({ ...s, [key]: value }))
        try {
            if (key === 'feedbackLanguage') {
                await setOrgSetting('feedbackLanguage', value as PartnerService.OrganizationSettingsFeedbackLanguageEnum)
            } else {
                await setOrgSetting(key, value as boolean)
            }
        } catch (err) {
            setLocalSettings(prev)
            const errorMessage =
                err instanceof AxiosError
                    ? err.response?.data?.error ?? err.message
                    : err instanceof Error
                      ? err.message
                      : String(err)
            console.error('[OrganizationSettingsPage] handleSettingChange failed:', err)
            toast.error(`${t('organizationSettings.settingsUpdateFailed')}: ${errorMessage}`)
        }
    }

    const invitationEmails = localSettings?.invitationEmails
    const submissionEmails = localSettings?.submissionEmails
    const feedbackLanguage = localSettings?.feedbackLanguage as FeedbackLanguage | undefined

    return (
        <div className="flex flex-1 w-full justify-center">
            <div className="flex-1 max-w-4xl space-y-8 mt-8 mb-16 px-4">
                {/* Page Header */}
                <div className="space-y-2">
                    <h1 className="text-3xl font-bold text-foreground">
                        {t('organizationSettings.title')}
                    </h1>
                    <p className="text-muted-foreground">
                        {t('organizationSettings.description')}
                    </p>
                </div>

                {/* Settings Section */}
                <Card className="border-border">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-4">
                            {t('common.settings')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <SettingRow
                            Icon={Mail}
                            title={t('organizationSettings.invitationEmails')}
                            description={
                                invitationEmails
                                    ? tP('invitationEmailsDescriptionON')
                                    : tP('invitationEmailsDescriptionOFF')
                            }
                        >
                            <Switch
                                id="invitationEmails"
                                checked={invitationEmails ?? false}
                                onCheckedChange={(checked) => handleSettingChange('invitationEmails', checked)}
                                disabled={isLoading}
                            />
                        </SettingRow>
                        <SettingRow
                            Icon={Bell}
                            title={t('organizationSettings.submissionNotifications')}
                            description={tP('submissionNotificationsDescription')}
                        >
                            <Switch
                                id="submissionEmails"
                                checked={submissionEmails ?? false}
                                onCheckedChange={(checked) => handleSettingChange('submissionEmails', checked)}
                                disabled={isLoading}
                            />
                        </SettingRow>
                        <SettingRow
                            Icon={Languages}
                            title={t('organizationSettings.feedbackLanguage')}
                            description={t('organizationSettings.feedbackLanguageDescription')}
                        >
                            <LanguagePicker
                                value={feedbackLanguage}
                                onValueChange={(v) =>
                                    handleSettingChange(
                                        'feedbackLanguage',
                                        v as PartnerService.OrganizationSettingsFeedbackLanguageEnum
                                    )
                                }
                                disabled={isLoading}
                            />
                        </SettingRow>
                    </CardContent>
                </Card>
            </div>
        </div>
    )
}
